import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  Database,
  Mail,
  Globe,
  DollarSign,
  GitBranch,
  TreePine,
  TestTube,
  Key,
  Activity,
  Clock,
  Users,
  Coffee,
  Cake
} from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const adminTools = [
    {
      title: 'Secrets Manager',
      path: '/admin/secrets',
      icon: Key,
      description: 'Manage API keys and environment variables',
      color: 'bg-red-500'
    },
    {
      title: 'CRUD Test',
      path: '/admin/crud-test',
      icon: Database,
      description: 'Test database operations and CRUD functionality',
      color: 'bg-blue-500'
    },
    {
      title: 'Email Test',
      path: '/admin/email-test',
      icon: Mail,
      description: 'Test email sending and templates',
      color: 'bg-green-500'
    },
    {
      title: 'DNS Checker',
      path: '/admin/dns',
      icon: Globe,
      description: 'Check DNS configuration and domain settings',
      color: 'bg-slate-500'
    },
    {
      title: 'Monetization Flow',
      path: '/admin/monetization',
      icon: DollarSign,
      description: 'View monetization flow diagram',
      color: 'bg-yellow-500'
    },
    {
      title: 'Decision Tree',
      path: '/admin/decision-tree',
      icon: GitBranch,
      description: 'View static decision tree visualization',
      color: 'bg-indigo-500'
    },
    {
      title: 'Interactive Tree',
      path: '/admin/interactive-tree',
      icon: TreePine,
      description: 'Interactive decision tree with API logger',
      color: 'bg-teal-500'
    },
    {
      title: 'Testing Flow',
      path: '/test',
      icon: TestTube,
      description: 'Component testing and validation',
      color: 'bg-pink-500'
    }
  ];

  const appRoutes = [
    {
      title: 'Birthday Card Flow',
      path: '/',
      icon: Cake,
      description: 'Main card sending experience',
      color: 'bg-orange-500'
    },
    {
      title: 'Birthday Hub',
      path: '/hub',
      icon: Users,
      description: 'Manage contacts and bulk operations',
      color: 'bg-cyan-500'
    },
    {
      title: 'Landing Page',
      path: '/landing',
      icon: Coffee,
      description: 'Original landing page design',
      color: 'bg-amber-500'
    },
    {
      title: 'Animated Cards (Old)',
      path: '/cards-old',
      icon: Activity,
      description: 'Previous card animation system',
      color: 'bg-rose-500'
    }
  ];

  const stats = [
    { label: 'Total Routes', value: adminTools.length + appRoutes.length },
    { label: 'Admin Tools', value: adminTools.length },
    { label: 'User Routes', value: appRoutes.length },
    { label: 'Last Deploy', value: new Date().toLocaleTimeString() }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-blue-400" />
            <h1 className="text-4xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-400" />
            <span className="text-gray-300 text-sm">{new Date().toLocaleString()}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
              <p className="text-gray-300 text-sm mb-1">{stat.label}</p>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
            </div>
          ))}
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Admin Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {adminTools.map((tool) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.path}
                  to={tool.path}
                  className="group relative bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <div className={`absolute top-0 right-0 w-20 h-20 ${tool.color} opacity-20 rounded-full blur-3xl group-hover:opacity-30 transition-opacity`} />
                  <div className="flex items-start gap-4">
                    <div className={`p-3 ${tool.color} rounded-lg bg-opacity-20 group-hover:bg-opacity-30 transition-all`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-2">{tool.title}</h3>
                      <p className="text-gray-300 text-sm">{tool.description}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div>
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Coffee className="w-6 h-6" />
            Application Routes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {appRoutes.map((route) => {
              const Icon = route.icon;
              return (
                <Link
                  key={route.path}
                  to={route.path}
                  className="group relative bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                >
                  <div className={`absolute top-0 right-0 w-20 h-20 ${route.color} opacity-20 rounded-full blur-3xl group-hover:opacity-30 transition-opacity`} />
                  <div className="flex items-start gap-4">
                    <div className={`p-3 ${route.color} rounded-lg bg-opacity-20 group-hover:bg-opacity-30 transition-all`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-2">{route.title}</h3>
                      <p className="text-gray-300 text-sm">{route.description}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm">
            Birthday Coffee Admin Panel • {new Date().getFullYear()} •
            <span className="ml-2 text-blue-400">Environment: Development</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;